/**
 * AG-UI (Agent-User Interaction Protocol) Types for Solomon Frontend
 *
 * Based on AG-UI Protocol Specification v1.0
 * See: docs/solomon/SOLOMON_AG_UI_PROTOCOL.md
 *
 * This file mirrors the backend AG-UI types for frontend consumption.
 */

// ============================================================================
// Event Type Enumeration (19 standard + 2 extension)
// ============================================================================

export const AGUIEventType = {
  // Lifecycle Events
  RUN_STARTED: 'RUN_STARTED',
  RUN_FINISHED: 'RUN_FINISHED',
  RUN_ERROR: 'RUN_ERROR',
  STEP_STARTED: 'STEP_STARTED',
  STEP_FINISHED: 'STEP_FINISHED',

  // Text Message Events
  TEXT_MESSAGE_START: 'TEXT_MESSAGE_START',
  TEXT_MESSAGE_CONTENT: 'TEXT_MESSAGE_CONTENT',
  TEXT_MESSAGE_END: 'TEXT_MESSAGE_END',
  TEXT_MESSAGE_CHUNK: 'TEXT_MESSAGE_CHUNK',

  // Tool Call Events
  TOOL_CALL_START: 'TOOL_CALL_START',
  TOOL_CALL_ARGS: 'TOOL_CALL_ARGS',
  TOOL_CALL_END: 'TOOL_CALL_END',
  TOOL_CALL_CHUNK: 'TOOL_CALL_CHUNK',
  TOOL_CALL_RESULT: 'TOOL_CALL_RESULT',

  // State Management Events
  STATE_SNAPSHOT: 'STATE_SNAPSHOT',
  STATE_DELTA: 'STATE_DELTA',
  MESSAGES_SNAPSHOT: 'MESSAGES_SNAPSHOT',

  // Activity Events
  ACTIVITY_SNAPSHOT: 'ACTIVITY_SNAPSHOT',
  ACTIVITY_DELTA: 'ACTIVITY_DELTA',

  // Extension Events
  RAW: 'RAW',
  CUSTOM: 'CUSTOM',
} as const;

export type AGUIEventType = (typeof AGUIEventType)[keyof typeof AGUIEventType];

/** Value type for type guard arrays */
export type AGUIEventTypeValue = AGUIEventType;

// ============================================================================
// Solomon Custom Event Names
// ============================================================================

export const SolomonCustomEventName = {
  // UI Co-Pilot Events
  UI_ACTION_PLAN: 'UI_ACTION_PLAN',
  UI_ACTION_START: 'UI_ACTION_START',
  UI_ACTION_COMPLETE: 'UI_ACTION_COMPLETE',
  UI_ACTION_ERROR: 'UI_ACTION_ERROR',

  // Human-in-the-Loop Events
  CONFIRMATION_REQUIRED: 'CONFIRMATION_REQUIRED',
  AWAITING_INPUT: 'AWAITING_INPUT',

  // Page Context Events
  PAGE_CONTEXT_UPDATE: 'PAGE_CONTEXT_UPDATE',

  // Thinking/Reasoning (Extended Thinking)
  THINKING_START: 'THINKING_START',
  THINKING_CONTENT: 'THINKING_CONTENT',
  THINKING_END: 'THINKING_END',
} as const;

export type SolomonCustomEventName = (typeof SolomonCustomEventName)[keyof typeof SolomonCustomEventName];

/** Value type for type guard arrays */
export type SolomonCustomEventNameValue = SolomonCustomEventName;

// ============================================================================
// RFC 6902 JSON Patch Types
// ============================================================================

export type JsonPatchOp = 'add' | 'remove' | 'replace' | 'move' | 'copy' | 'test';

export type JsonPatchOperation = {
  op: JsonPatchOp;
  path: string;
  value?: unknown;
  from?: string;
};

// ============================================================================
// Message Types
// ============================================================================

export type MessageRole = 'user' | 'assistant' | 'system' | 'tool' | 'developer';

export type ContentPart =
  | { type: 'text'; text: string }
  | { type: 'image'; image: string; mimeType?: string }
  | { type: 'tool_use'; id: string; name: string; input: Record<string, unknown> }
  | { type: 'tool_result'; tool_use_id: string; content: string };

export type Message = {
  id: string;
  role: MessageRole;
  content: string | ContentPart[];
  toolCallId?: string;
  name?: string;
  timestamp?: number;
};

// ============================================================================
// Base Event Interface
// ============================================================================

export type BaseEvent = {
  type: AGUIEventType;
  timestamp?: number;
  rawEvent?: unknown;
};

// ============================================================================
// Lifecycle Events
// ============================================================================

export type RunStartedEvent = BaseEvent & {
  type: typeof AGUIEventType.RUN_STARTED;
  threadId: string;
  runId: string;
  parentRunId?: string;
};

export type RunFinishedEvent = BaseEvent & {
  type: typeof AGUIEventType.RUN_FINISHED;
  threadId: string;
  runId: string;
  result?: unknown;
};

export type RunErrorEvent = BaseEvent & {
  type: typeof AGUIEventType.RUN_ERROR;
  threadId: string;
  runId: string;
  message: string;
  code?: string;
  details?: unknown;
};

export type StepStartedEvent = BaseEvent & {
  type: typeof AGUIEventType.STEP_STARTED;
  stepName: string;
  stepId?: string;
};

export type StepFinishedEvent = BaseEvent & {
  type: typeof AGUIEventType.STEP_FINISHED;
  stepName: string;
  stepId?: string;
  result?: unknown;
};

// ============================================================================
// Text Message Events
// ============================================================================

export type TextMessageStartEvent = BaseEvent & {
  type: typeof AGUIEventType.TEXT_MESSAGE_START;
  messageId: string;
  role: 'assistant';
};

export type TextMessageContentEvent = BaseEvent & {
  type: typeof AGUIEventType.TEXT_MESSAGE_CONTENT;
  messageId: string;
  delta: string;
};

export type TextMessageEndEvent = BaseEvent & {
  type: typeof AGUIEventType.TEXT_MESSAGE_END;
  messageId: string;
};

export type TextMessageChunkEvent = BaseEvent & {
  type: typeof AGUIEventType.TEXT_MESSAGE_CHUNK;
  messageId?: string;
  role?: 'assistant' | 'developer' | 'system';
  delta: string;
};

// ============================================================================
// Tool Call Events
// ============================================================================

export type ToolCallStartEvent = BaseEvent & {
  type: typeof AGUIEventType.TOOL_CALL_START;
  toolCallId: string;
  toolCallName: string;
  parentMessageId?: string;
};

export type ToolCallArgsEvent = BaseEvent & {
  type: typeof AGUIEventType.TOOL_CALL_ARGS;
  toolCallId: string;
  delta: string;
};

export type ToolCallEndEvent = BaseEvent & {
  type: typeof AGUIEventType.TOOL_CALL_END;
  toolCallId: string;
};

export type ToolCallChunkEvent = BaseEvent & {
  type: typeof AGUIEventType.TOOL_CALL_CHUNK;
  toolCallId?: string;
  toolCallName?: string;
  parentMessageId?: string;
  delta: string;
};

export type ToolCallResultEvent = BaseEvent & {
  type: typeof AGUIEventType.TOOL_CALL_RESULT;
  messageId: string;
  toolCallId: string;
  content: string;
  role?: 'tool';
};

// ============================================================================
// State Management Events
// ============================================================================

export type StateSnapshotEvent = BaseEvent & {
  type: typeof AGUIEventType.STATE_SNAPSHOT;
  snapshot: unknown;
};

export type StateDeltaEvent = BaseEvent & {
  type: typeof AGUIEventType.STATE_DELTA;
  delta: JsonPatchOperation[];
};

export type MessagesSnapshotEvent = BaseEvent & {
  type: typeof AGUIEventType.MESSAGES_SNAPSHOT;
  messages: Message[];
};

// ============================================================================
// Activity Events
// ============================================================================

export type ActivitySnapshotEvent = BaseEvent & {
  type: typeof AGUIEventType.ACTIVITY_SNAPSHOT;
  messageId: string;
  activityType: string;
  content: unknown;
  replace?: boolean;
};

export type ActivityDeltaEvent = BaseEvent & {
  type: typeof AGUIEventType.ACTIVITY_DELTA;
  messageId: string;
  activityType: string;
  patch: JsonPatchOperation[];
};

// ============================================================================
// Extension Events
// ============================================================================

export type RawEvent = BaseEvent & {
  type: typeof AGUIEventType.RAW;
  event: unknown;
  source?: string;
};

export type CustomEvent<T = unknown> = BaseEvent & {
  type: typeof AGUIEventType.CUSTOM;
  name: string;
  value: T;
};

// ============================================================================
// Solomon Custom Event Payloads
// ============================================================================

/** UI Action types for co-pilot */
export type UIActionType = 'click' | 'fill' | 'select' | 'check' | 'uncheck' | 'hover' | 'scroll' | 'navigate' | 'wait';

/** Single UI action */
export type UIAction = {
  type: UIActionType;
  ref: string;
  value?: string | number | boolean;
  description?: string;
};

/** UI_ACTION_PLAN payload */
export type UIActionPlanPayload = {
  planId: string;
  explanation: string;
  actions: UIAction[];
  estimatedDurationMs: number;
  requiresConfirmation: boolean;
};

/** UI_ACTION_START payload */
export type UIActionStartPayload = {
  planId: string;
  actionIndex: number;
  action: UIAction;
};

/** UI_ACTION_COMPLETE payload */
export type UIActionCompletePayload = {
  planId: string;
  actionIndex: number;
  success: boolean;
  durationMs: number;
  screenshot?: string;
};

/** UI_ACTION_ERROR payload */
export type UIActionErrorPayload = {
  planId: string;
  actionIndex: number;
  error: string;
  recoverable: boolean;
  suggestedAction?: string;
};

/** Action button style */
export type ActionButtonStyle = 'primary' | 'secondary' | 'danger';

/** CONFIRMATION_REQUIRED payload */
export type ConfirmationRequiredPayload = {
  confirmationId: string;
  message: string;
  context?: string;
  actions: Array<{
    id: string;
    label: string;
    style: ActionButtonStyle;
  }>;
  timeout?: number;
  defaultAction?: string;
};

/** Input type for AWAITING_INPUT */
export type AwaitingInputType = 'text' | 'select' | 'multiselect' | 'date' | 'file';

/** AWAITING_INPUT payload */
export type AwaitingInputPayload = {
  inputId: string;
  prompt: string;
  inputType: AwaitingInputType;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  required?: boolean;
};

/** Thinking content payload */
export type ThinkingContentPayload = {
  content: string;
  isFinal?: boolean;
};

// ============================================================================
// Page Snapshot Types (for UI Co-pilot)
// ============================================================================

/** Accessibility node from page snapshot */
export type AXNode = {
  nodeId: string;
  role: string;
  name?: string;
  value?: string;
  description?: string;
  focused?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  children?: AXNode[];
};

/** UI Element with actionable reference */
export type UIElement = {
  ref: string;
  role: string;
  name?: string;
  value?: string;
  tagName: string;
  attributes: Record<string, string>;
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  isInteractive: boolean;
  isVisible: boolean;
};

/** Form field snapshot */
export type FormFieldSnapshot = {
  name: string;
  type: string;
  value?: string | boolean | string[];
  label?: string;
  required?: boolean;
  validation?: {
    isValid: boolean;
    errorMessage?: string;
  };
};

/** Form snapshot */
export type FormSnapshot = {
  id: string;
  name?: string;
  action?: string;
  method?: string;
  fields: FormFieldSnapshot[];
};

/** Validation error in page */
export type ValidationError = {
  fieldName: string;
  message: string;
  ref?: string;
};

/** Complete page snapshot */
export type PageSnapshot = {
  id: string;
  url: string;
  title: string;
  timestamp: string;
  accessibilityTree: AXNode[];
  elements: Record<string, UIElement>;
  forms: FormSnapshot[];
  errors: ValidationError[];
};

// ============================================================================
// Union Types for All Events
// ============================================================================

/** All standard AG-UI events */
export type AGUIEvent =
  | RunStartedEvent
  | RunFinishedEvent
  | RunErrorEvent
  | StepStartedEvent
  | StepFinishedEvent
  | TextMessageStartEvent
  | TextMessageContentEvent
  | TextMessageEndEvent
  | TextMessageChunkEvent
  | ToolCallStartEvent
  | ToolCallArgsEvent
  | ToolCallEndEvent
  | ToolCallChunkEvent
  | ToolCallResultEvent
  | StateSnapshotEvent
  | StateDeltaEvent
  | MessagesSnapshotEvent
  | ActivitySnapshotEvent
  | ActivityDeltaEvent
  | RawEvent
  | CustomEvent;

/** Solomon-specific custom events */
export type SolomonCustomEvent =
  | CustomEvent<UIActionPlanPayload>
  | CustomEvent<UIActionStartPayload>
  | CustomEvent<UIActionCompletePayload>
  | CustomEvent<UIActionErrorPayload>
  | CustomEvent<ConfirmationRequiredPayload>
  | CustomEvent<AwaitingInputPayload>
  | CustomEvent<PageSnapshot>
  | CustomEvent<ThinkingContentPayload>;

/** All Solomon events (AG-UI + custom) */
export type SolomonAGUIEvent = AGUIEvent | SolomonCustomEvent;

// ============================================================================
// Type Guards
// ============================================================================

/** Check if event is a lifecycle event */
export function isLifecycleEvent(
  event: AGUIEvent
): event is RunStartedEvent | RunFinishedEvent | RunErrorEvent | StepStartedEvent | StepFinishedEvent {
  const lifecycleTypes: AGUIEventTypeValue[] = [
    AGUIEventType.RUN_STARTED,
    AGUIEventType.RUN_FINISHED,
    AGUIEventType.RUN_ERROR,
    AGUIEventType.STEP_STARTED,
    AGUIEventType.STEP_FINISHED,
  ];
  return lifecycleTypes.includes(event.type);
}

/** Check if event is a text message event */
export function isTextMessageEvent(
  event: AGUIEvent
): event is TextMessageStartEvent | TextMessageContentEvent | TextMessageEndEvent | TextMessageChunkEvent {
  const textMessageTypes: AGUIEventTypeValue[] = [
    AGUIEventType.TEXT_MESSAGE_START,
    AGUIEventType.TEXT_MESSAGE_CONTENT,
    AGUIEventType.TEXT_MESSAGE_END,
    AGUIEventType.TEXT_MESSAGE_CHUNK,
  ];
  return textMessageTypes.includes(event.type);
}

/** Check if event is a tool call event */
export function isToolCallEvent(
  event: AGUIEvent
): event is ToolCallStartEvent | ToolCallArgsEvent | ToolCallEndEvent | ToolCallChunkEvent | ToolCallResultEvent {
  const toolCallTypes: AGUIEventTypeValue[] = [
    AGUIEventType.TOOL_CALL_START,
    AGUIEventType.TOOL_CALL_ARGS,
    AGUIEventType.TOOL_CALL_END,
    AGUIEventType.TOOL_CALL_CHUNK,
    AGUIEventType.TOOL_CALL_RESULT,
  ];
  return toolCallTypes.includes(event.type);
}

/** Check if event is a state management event */
export function isStateEvent(event: AGUIEvent): event is StateSnapshotEvent | StateDeltaEvent | MessagesSnapshotEvent {
  const stateTypes: AGUIEventTypeValue[] = [
    AGUIEventType.STATE_SNAPSHOT,
    AGUIEventType.STATE_DELTA,
    AGUIEventType.MESSAGES_SNAPSHOT,
  ];
  return stateTypes.includes(event.type);
}

/** Check if event is a custom event */
export function isCustomEvent(event: AGUIEvent): event is CustomEvent {
  return event.type === AGUIEventType.CUSTOM;
}

/** Check if custom event is a Solomon UI action event */
export function isUIActionEvent(
  event: CustomEvent
): event is
  | CustomEvent<UIActionPlanPayload>
  | CustomEvent<UIActionStartPayload>
  | CustomEvent<UIActionCompletePayload>
  | CustomEvent<UIActionErrorPayload> {
  const uiActionNames: SolomonCustomEventNameValue[] = [
    SolomonCustomEventName.UI_ACTION_PLAN,
    SolomonCustomEventName.UI_ACTION_START,
    SolomonCustomEventName.UI_ACTION_COMPLETE,
    SolomonCustomEventName.UI_ACTION_ERROR,
  ];
  return uiActionNames.includes(event.name as SolomonCustomEventNameValue);
}

/** Check if custom event requires user confirmation */
export function isConfirmationEvent(event: CustomEvent): event is CustomEvent<ConfirmationRequiredPayload> {
  return event.name === SolomonCustomEventName.CONFIRMATION_REQUIRED;
}

/** Check if custom event is awaiting user input */
export function isAwaitingInputEvent(event: CustomEvent): event is CustomEvent<AwaitingInputPayload> {
  return event.name === SolomonCustomEventName.AWAITING_INPUT;
}

// ============================================================================
// Event Parsing Utilities
// ============================================================================

/** Parse a raw SSE data string into an AG-UI event */
export function parseAGUIEvent(data: string): AGUIEvent | null {
  try {
    const event = JSON.parse(data) as AGUIEvent;
    if (!event.type) {
      return null;
    }
    return event;
  } catch {
    return null;
  }
}

/** Extract the Solomon custom event name and payload */
export function extractCustomPayload<T>(event: CustomEvent<T>): { name: string; payload: T } {
  return {
    name: event.name,
    payload: event.value,
  };
}

// ============================================================================
// Frontend State Types
// ============================================================================

/** State for tracking current AG-UI run */
export type AGUIRunState = {
  threadId: string | null;
  runId: string | null;
  isRunning: boolean;
  error: RunErrorEvent | null;
  startedAt: number | null;
  finishedAt: number | null;
};

/** State for tracking current message being streamed */
export type AGUIMessageState = {
  messageId: string | null;
  role: MessageRole;
  content: string;
  isStreaming: boolean;
};

/** State for tracking tool calls */
export type AGUIToolCallState = {
  toolCallId: string;
  toolCallName: string;
  args: string;
  result: string | null;
  status: 'pending' | 'streaming_args' | 'executing' | 'completed' | 'error';
};

/** State for tracking UI action plans */
export type AGUIActionPlanState = {
  planId: string;
  explanation: string;
  actions: UIAction[];
  currentActionIndex: number;
  status: 'pending_confirmation' | 'executing' | 'completed' | 'error' | 'cancelled';
  results: Array<{
    actionIndex: number;
    success: boolean;
    durationMs?: number;
    error?: string;
  }>;
};

/** Combined AG-UI frontend state */
export type AGUIState = {
  run: AGUIRunState;
  currentMessage: AGUIMessageState | null;
  toolCalls: Map<string, AGUIToolCallState>;
  actionPlans: Map<string, AGUIActionPlanState>;
  pendingConfirmation: ConfirmationRequiredPayload | null;
  pendingInput: AwaitingInputPayload | null;
  activities: Map<string, unknown>;
};

/** Initial AG-UI state */
export const initialAGUIState: AGUIState = {
  run: {
    threadId: null,
    runId: null,
    isRunning: false,
    error: null,
    startedAt: null,
    finishedAt: null,
  },
  currentMessage: null,
  toolCalls: new Map(),
  actionPlans: new Map(),
  pendingConfirmation: null,
  pendingInput: null,
  activities: new Map(),
};
